<template>
  <div
    v-if="foreigner"
    v-loading="!foreigner"
    class="tw-max-w-4xl tw-mx-auto tw-h-full tw-flex tw-flex-col"
  >
    <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
      <div
        class="tw-flex tw-items-center"
      >
        <i-user-avatar
          :avatar="foreigner.avatar_url && foreigner.avatar_url['128'] && `${$baseHost}/${foreigner.avatar_url['128']}`"
          :fullname="foreigner.fullname"
          size="large"
          class="tw-mr-3"
        />
        <div class="tw-flex">
          <strong class="tw-block tw-text-h2 tw-leading-7 tw-mb-px">
            {{ foreigner.fullname }}
          </strong>
          <ForeignerFamilyPopoverList
            v-if="foreigner && foreigner.family_profiles && foreigner.family_profiles.length"
            :family="foreigner.family_profiles.map((item) => ({
              avatar_url: item.avatar_url,
              relation_with_id: item.id,
              relation_with_fullname: item.fullname,
              relation_type: item.relation_type_name,
              relation_type_name: $store.getters['v2/shared/enums/get']('family_relations', item.relation_type) ? $store.getters['v2/shared/enums/get']('family_relations', item.relation_type).name : '',
            }))"
            :foreigner-id="foreigner.id"
            store-path="v2/foreigner/messaging"
            :link="(id) => `/foreigner/${id}`"
          >
            <button class="tw-text-h2 tw-leading-7 tw-text-primary dark:tw-text-dm-primary tw-ml-2 tw-flex tw-items-center">
              +{{ foreigner.family_profiles.length }}
              <icon-angle-down
                class="tw-ml-1"
              />
            </button>
          </ForeignerFamilyPopoverList>
        </div>
      </div>

      <div
        class="tw-flex tw-items-center tw-cursor-pointer"
        role="button"
        tabindex="0"
        aria-label="Open messaging popover"
        @keydown.enter="openMessagingPopover"
        @click.stop="openMessagingPopover"
      >
        <i-user-avatar
          :avatar="foreigner.assigned_to_avatar_url && foreigner.assigned_to_avatar_url['128'] && `${$baseHost}/${foreigner.assigned_to_avatar_url['128']}`"
          :fullname="foreigner.assigned_to_fullname"
          size="small"
          class="tw-mr-3"
        />
        <div class="tw-flex tw-flex-col tw-gap-1 tw-text-base tw-leading-5">
          <span class="tw-block tw-text-gray-dark dark:tw-text-dm-gray-dark tw-tracking-normal">
            {{ $t('foreigners.basic_info.assigned_to') }}
          </span>
          <span class="tw-inline-block tw-font-medium tw-tracking-wider">
            {{ foreigner.assigned_to_fullname }}
          </span>
        </div>
      </div>
    </div>

    <div class="tw-border-b tw-border-gray dark:tw-border-dm-gray tw-mb-6">
      <div class="tw-container tw-mx-auto">
        <el-tabs
          v-model="tab"
        >
          <el-tab-pane
            :label="$t('foreigners.tabs.permits')"
            name="permits"
          />
          <el-tab-pane
            :label="$t('foreigners.tabs.questionnaires')"
            name="questionnaires"
          />
        </el-tabs>
      </div>
    </div>

    <section
      v-if="tab === 'permits'"
      class="tw-h-full tw-w-full"
    >
      <template v-if="permits.length > 0">
        <div
          v-if="permitsInProgress.length !== 0"
          class="tw-mb-16"
        >
          <h1 class="tw-mb-4 tw-leading-[38px] tw-tracking-wider">
            {{ $t('foreigners.view.permits_in_process') }}
          </h1>
          <div class="tw-flex tw-flex-col tw-gap-4">
            <ForeignerPermitBox
              v-for="permit in permitsInProgress"
              :key="permit.id"
              :permit="permit"
            />
          </div>
        </div>

        <div v-if="permitsObtained.length !== 0">
          <h1 class="tw-mb-4 tw-leading-[38px] tw-tracking-wider">
            {{ $t('foreigners.view.permits_obtained') }}
          </h1>
          <div class="tw-flex tw-flex-col tw-gap-4">
            <ForeignerPermitBox
              v-for="permit in permitsObtained"
              :key="permit.id"
              :permit="permit"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="tw-h-full tw-flex tw-items-center tw-justify-center">
          {{ $t('common.search.empty') }}
        </div>
      </template>
    </section>
    <section
      v-if="tab === 'questionnaires'"
      class="tw-h-full tw-w-full"
    >
      <ForeignerQuestionnaires />
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ForeignerFamilyPopoverList from './ForeignerFamilyPopoverList.vue';
import ForeignerPermitBox from '@/components/Foreigner/Permits/ForeignerPermitBox.vue';
import ForeignerQuestionnaires from '@/components/Foreigner/Questionnaires/ForeignerQuestionnaires.vue';

export default {
  components: {
    ForeignerQuestionnaires,
    ForeignerFamilyPopoverList,
    ForeignerPermitBox,
  },
  data() {
    return {
      tab: 'permits',
    };
  },
  computed: {
    ...mapState({
      foreigner: (state) => state.v2.foreigner.foreigner.foreigner,
      permits: (state) => state.v2.foreigner.permits.permits,
      me: (state) => state.v2.shared.profile.me,
    }),
    permitsInProgress() {
      return this.permits?.filter((permit) => permit.permit_status !== 30) ?? [];
    },
    permitsObtained() {
      return this.permits?.filter((permit) => permit.permit_status === 30) ?? [];
    },
  },

  watch: {
    '$route.params.fid': {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },

  methods: {
    load() {
      this.$store.dispatch('v2/foreigner/foreigner/load', this.$route.params.fid || this.me.id);
      this.$store.dispatch('v2/foreigner/permits/load', this.$route.params.fid || this.me.id);
    },
    openMessagingPopover() {
      window.dispatchEvent(new CustomEvent('foreignerMessagingPopoverOpen'));
    },
  },
};
</script>
